import {
  bulkPermissionResponseKey,
  normalizeAclActionForPolicy,
  type ACLPermission,
  type PermissionCheckRequest,
} from "../api/aclApi";

const permissionCache = new Map<
  string,
  {
    allowed: boolean;
    reason: string;
    matchedPermissions: ACLPermission[];
    timestamp: number;
  }
>();

export const PERMISSION_CACHE_DURATION_MS = 60000;

export function permissionCacheKeyForRequest(req: PermissionCheckRequest): string {
  const ownerKey = req.itemOwnerId || req.itemCreatorId || "";
  const ca = normalizeAclActionForPolicy(req.action as string);
  return `${ca}:${req.targetType}:${req.targetId}:${req.collectionUid ?? ""}:${req.fieldKey || ""}:${ownerKey}`;
}

export function warmPermissionCacheFromBulkResults(
  requests: PermissionCheckRequest[],
  bulkResults: Record<string, boolean>
): void {
  const now = Date.now();
  for (const req of requests) {
    const key = permissionCacheKeyForRequest(req);
    const allowed = bulkResults[bulkPermissionResponseKey(req)] ?? false;
    permissionCache.set(key, {
      allowed,
      reason: allowed ? "Allowed" : "Denied",
      matchedPermissions: [],
      timestamp: now,
    });
  }
}

export function getCachedPermissionEntry(
  req: PermissionCheckRequest,
  maxAgeMs: number
): { allowed: boolean; reason: string; matchedPermissions: ACLPermission[] } | null {
  const key = permissionCacheKeyForRequest(req);
  const cached = permissionCache.get(key);
  const now = Date.now();
  if (!cached || now - cached.timestamp >= maxAgeMs) {
    return null;
  }
  return {
    allowed: cached.allowed,
    reason: cached.reason,
    matchedPermissions: cached.matchedPermissions,
  };
}

export function getPermissionCache(): typeof permissionCache {
  return permissionCache;
}

let permissionCacheEpoch = 0;
const invalidationListeners = new Set<() => void>();

export function getPermissionCacheEpoch(): number {
  return permissionCacheEpoch;
}

/** Subscribe to cache clears (ACL reload, config reload, group changes). */
export function subscribePermissionCacheInvalidation(listener: () => void): () => void {
  invalidationListeners.add(listener);
  return () => {
    invalidationListeners.delete(listener);
  };
}

export function clearPermissionCacheStore(): void {
  permissionCache.clear();
  permissionCacheEpoch += 1;
  for (const listener of invalidationListeners) {
    try {
      listener();
    } catch {
      /* listener must not break invalidation */
    }
  }
}
